import { SmsCredentialMode, type SmsSettingsDTO, type SmsSettingsInput } from '@today/types'
import { Button, Radio, RadioGroup } from '@today/ui-primitives'
import { useState } from 'react'
import {
	draftOf,
	EMPTY_CREDENTIALS,
	type SmsSettingsDraft,
	type SmsSettingsFieldErrors,
	submissionOf,
} from './sms-settings-draft'
import { ConfirmManagedDialog, CredentialFields, GroupError, SenderAndPolicyFields } from './sms-settings-fields'

/**
 * Props for {@link SmsSettingsForm}.
 *
 * @property settings - The stored settings; the form is seeded from them once (key the form on `updatedAt` to re-seed).
 * @property readOnly - The manifest grants READ: every input is disabled and no save is offered.
 * @property isBusy - A save is in flight; saving is disabled.
 * @property fieldErrors - API field path → message, shown next to that field.
 * @property error - A refusal to announce for the whole form.
 * @property onSave - Reports the settings to store (a whole replacement).
 */
export interface SmsSettingsFormProps {
	settings: SmsSettingsDTO
	readOnly?: boolean
	isBusy?: boolean
	fieldErrors?: SmsSettingsFieldErrors
	error?: string | null
	onSave: (input: SmsSettingsInput) => void
}

const NO_ERRORS: SmsSettingsFieldErrors = {}

interface ModeChoiceProps {
	mode: SmsCredentialMode
	/** The tenant's saved mode is BYO with stored credentials: leaving it needs a confirmation. */
	storedByo: boolean
	readOnly: boolean
	errors: SmsSettingsFieldErrors
	update: (patch: Partial<SmsSettingsDraft>) => void
}

/** Managed or the tenant's own Twilio account; leaving a stored BYO asks to confirm the credentials' deletion. */
function ModeChoice({ mode, storedByo, readOnly, errors, update }: ModeChoiceProps) {
	const [confirming, setConfirming] = useState(false)
	const choose = (value: string) => {
		const next = value as SmsCredentialMode
		if (next === SmsCredentialMode.MANAGED && storedByo) setConfirming(true)
		else update({ mode: next })
	}
	const confirm = () => {
		setConfirming(false)
		update({ mode: SmsCredentialMode.MANAGED, credentials: EMPTY_CREDENTIALS })
	}
	return (
		<div className="flex flex-col gap-1">
			<p className="text-ds-sm font-semibold text-ds-text">Sending account</p>
			<RadioGroup aria-label="Sending account" value={mode} onChange={choose} isDisabled={readOnly}>
				<Radio value={SmsCredentialMode.MANAGED}>Managed</Radio>
				<Radio value={SmsCredentialMode.BYO}>Bring your own Twilio account</Radio>
			</RadioGroup>
			<GroupError path="mode" errors={errors} />
			<ConfirmManagedDialog isOpen={confirming} onCancel={() => setConfirming(false)} onConfirm={confirm} />
		</div>
	)
}

/**
 * The tenant's SMS settings (TO-383 REQ-014): how SMS are sent (TODAY's
 * account or the tenant's own Twilio), the sender, and the send policy —
 * allowed countries, a monthly spend cap and the notification types sent by SMS.
 *
 * Pure: the page's hook saves; every rule is the server's, and a refusal
 * arrives here as `fieldErrors` (by API path) or `error`.
 */
export function SmsSettingsForm({
	settings,
	readOnly = false,
	isBusy = false,
	fieldErrors = NO_ERRORS,
	error,
	onSave,
}: SmsSettingsFormProps) {
	const [draft, setDraft] = useState(() => draftOf(settings))
	const update = (patch: Partial<SmsSettingsDraft>) => setDraft((current) => ({ ...current, ...patch }))
	const storedByo = settings.mode === SmsCredentialMode.BYO && settings.hasCredentials

	return (
		<section className="flex flex-col gap-6" aria-label="SMS settings">
			{error ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{error}
				</p>
			) : null}
			<ModeChoice mode={draft.mode} storedByo={storedByo} readOnly={readOnly} errors={fieldErrors} update={update} />
			<fieldset disabled={readOnly} className="flex flex-col gap-6">
				{draft.mode === SmsCredentialMode.BYO ? (
					<CredentialFields
						credentials={draft.credentials}
						hasCredentials={settings.hasCredentials}
						errors={fieldErrors}
						onChange={(credentials) => update({ credentials })}
					/>
				) : null}
				<SenderAndPolicyFields draft={draft} errors={fieldErrors} update={update} />
			</fieldset>
			{readOnly ? null : (
				<div>
					<Button variant="primary" isDisabled={isBusy} onPress={() => onSave(submissionOf(draft))}>
						Save SMS settings
					</Button>
				</div>
			)}
		</section>
	)
}
